import { ReturnTypeEnum } from "@redotech/redo-model/return";
import {
  folderTypes,
  FolderTypesNames,
} from "@redotech/redo-model/views/folders";
import { RecycleView, ViewTypesNames } from "@redotech/redo-model/views/views";
import { createContext, memo } from "react";
import { CreateReturnViewFolderModal } from "../../return/views/create-return-view-folder-modal";
import { EditReturnViewFolderModal } from "../../return/views/edit-return-view-folder-modal";
import { useViewOrderingService } from "../shared/view-ordering-service-base";
import {
  RecycleViewsContext,
  ReloadRecycleViewsContext,
} from "./recycle-views";

export interface RecycleViewOrderingData {
  openCreateFolderModal: () => void;
  openEditFolderModal: (folderId: string) => void;
  handleDeleteFolder: (folderId: string) => void;
  updateOrdering: (
    updatedViews: RecycleView[],
    updatedFolders: folderTypes[FolderTypesNames.RECYCLE][],
  ) => void;
  saveOrdering: () => void;
  views: RecycleView[];
  folders: folderTypes[FolderTypesNames.RECYCLE][];
  notificationCounts: Record<string, number>;
}

export const RecycleViewOrderingContext = createContext<
  RecycleViewOrderingData | undefined
>(undefined);

export const RecycleViewOrderingService = memo(
  function RecycleViewOrderingService({
    children,
  }: {
    children: React.ReactNode;
  }) {
    const {
      views,
      folders,
      notificationCounts,
      showCreateFolderModal,
      showEditFolderModal,
      selectedFolder,
      reservedFolderNames,
      handleCreateFolder,
      handleEditFolder,
      handleDeleteFolder,
      updateOrdering,
      saveOrdering,
      openCreateFolderModal,
      openEditFolderModal,
      setShowCreateFolderModal,
      setShowEditFolderModal,
    } = useViewOrderingService({
      returnType: ReturnTypeEnum.RECYCLE,
      viewType: ViewTypesNames.RECYCLE,
      navDataContext: RecycleViewsContext,
      reloadNavDataContext: ReloadRecycleViewsContext,
    });

    return (
      <RecycleViewOrderingContext.Provider
        value={{
          openCreateFolderModal,
          openEditFolderModal,
          handleDeleteFolder,
          updateOrdering,
          saveOrdering,
          views,
          folders,
          notificationCounts,
        }}
      >
        {children}
        <CreateReturnViewFolderModal
          isOpen={showCreateFolderModal}
          onClose={() => setShowCreateFolderModal(false)}
          onSubmit={handleCreateFolder}
          reservedFolderNames={reservedFolderNames}
        />
        {selectedFolder && (
          <EditReturnViewFolderModal
            deleteFolder={handleDeleteFolder}
            isOpen={showEditFolderModal}
            onClose={() => setShowEditFolderModal(false)}
            onSubmit={handleEditFolder}
            reservedFolderNames={reservedFolderNames}
            selectedFolder={selectedFolder}
          />
        )}
      </RecycleViewOrderingContext.Provider>
    );
  },
);
